import React from "react";
import { Flex } from "rebass/styled-components";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { useRecoilValue } from "recoil";
import { MatchVodRequestedIds } from "MVVM/State/matchVod";
import type { TrayEntry } from "../../Hooks/useClipStatusRegistry";
import { useVodTrayMeta } from "../../Hooks/useVodTrayMeta";
import { TrackerCard } from "../TrackerCard";
import { MatchVodsCard } from "../MatchVodsCard";

type TrayShellProps = {
  entries: TrayEntry[];
  expanded: boolean;
  onToggleTray: () => void;
  onDismiss: (id: string) => void;
};

const cardMotion = {
  initial: { x: 60, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: 60, opacity: 0 },
  transition: { type: "spring", stiffness: 400, damping: 30 },
};

export const TrayShell = ({ entries, onDismiss }: TrayShellProps) => {
  const requestedMatchIds = useRecoilValue(MatchVodRequestedIds);
  const vodEntries = entries.filter(
    (e): e is Extract<TrayEntry, { kind: "vod" }> => e.kind === "vod",
  );
  const metaById = useVodTrayMeta(vodEntries.map((e) => e.montageId));

  if (entries.length === 0) return null;

  // Self-requested vods track like a normal card; auto-generated ones collapse
  // into a single grouped card so a session's worth of matches isn't spammy.
  const selfVods = vodEntries.filter((e) => {
    const matchId = metaById[e.montageId]?.matchId;
    return !!matchId && requestedMatchIds.includes(matchId);
  });
  const autoVods = vodEntries.filter((e) => !selfVods.includes(e));

  const dismissEntry = (entry: TrayEntry) => {
    if (entry.kind !== "clips") onDismiss(entry.montageId);
    entry.timelines.forEach((t) => onDismiss(t.trackId));
  };

  return (
    <TrayWrapper>
      <AnimatePresence initial={false}>
        {entries
          .filter((entry) => entry.kind !== "vod")
          .map((entry) => (
            <motion.div
              key={
                entry.kind === "clips" ? "standalone-clips" : entry.montageId
              }
              {...cardMotion}
            >
              <TrackerCard
                timelines={entry.timelines}
                montageId={
                  entry.kind === "montage" ? entry.montageId : undefined
                }
                onDismiss={() => dismissEntry(entry)}
              />
            </motion.div>
          ))}
        {selfVods.map((entry) => (
          <motion.div key={entry.montageId} {...cardMotion}>
            <TrackerCard
              timelines={entry.timelines}
              montageId={entry.montageId}
              vodMeta={metaById[entry.montageId]}
              onDismiss={() => dismissEntry(entry)}
            />
          </motion.div>
        ))}
        {autoVods.length > 0 && (
          <motion.div key="auto-match-vods" {...cardMotion}>
            <MatchVodsCard
              entries={autoVods}
              metaById={metaById}
              onDismissAll={() => autoVods.forEach(dismissEntry)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </TrayWrapper>
  );
};

const TrayWrapper = styled(Flex)`
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;
`;
